/**
 * Tab-strip hoisting.
 *
 * Routes used to render their own tab strip, which meant the strip for the level you were on
 * replaced the one above it, and any route that rendered none (every detail page) showed no
 * strip at all. Hoisting each strip onto a parent route keeps every level on screen, the shape
 * the dashboard first used at `organisation-dashboard`.
 *
 * Levels are declared as data (see TAB_LEVELS in routes.tsx) and applied recursively, so adding
 * a level is one line and no route literal has to mention TabsLayout. A route that genuinely
 * needs its own strip — per-child props like `fallbackTabSlug` cannot be shared by one hoisted
 * instance — opts out with `customTabs: true`.
 *
 * Deliberately free of React and of routes.tsx: the parent route object comes from an injected
 * factory. routes.tsx transitively imports the api layer, and axios ships ESM that Jest cannot
 * parse, so a test could not import it. Same reasoning as classifyUrl in utils/subsidiary.
 */

export type RouteLike = { path?: string; children?: RouteLike[]; customTabs?: boolean; hoistLevels?: TabLevel[]; hoistStrip?: { page: string; variant: TabVariant; parentPage?: string }; [key: string]: any }

export type TabVariant = 'secondary' | 'tertiary' | 'capsule'

export type TabLevel = {
  /** Path segment that opens this level, e.g. `nai-flow`. */
  segment: string
  /** The TabsLayout tab group that renders this strip. */
  page: string
  variant: TabVariant
  /**
   * Group only routes with no `:param` left below the segment — list pages, not detail pages.
   * A record page showing `Schedule · Report · Non-Conformance` reads as the wrong context, so
   * detail routes stay one level up and inherit only the strips above.
   */
  listOnly?: boolean
}

export type TabParentFactory = (args: { path: string; page: string; variant: TabVariant; parentPage?: string; children: RouteLike[] }) => RouteLike

export const PROJECT_SCOPE = ':name/:id'

const hasParam = (path: string) => path.split('/').some((part) => part.startsWith(':'))

/**
 * Finds the earliest segment matching any level, and splits the path around it.
 * `nai-flow/inspections/schedule` with the nai-flow level -> ['nai-flow', 'inspections/schedule'].
 * An exact section route such as `nai-flow` becomes the index child of the same parent. Keeping
 * its redirect inside that parent prevents the retained strip disappearing for one render while
 * React Router resolves the section's default child.
 */
export const splitAtLevel = (path: string, levels: TabLevel[]): [string, string, TabLevel] | null => {
  const parts = path.split('/')
  for (let i = 0; i < parts.length; i += 1) {
    const level = levels.find((candidate) => candidate.segment === parts[i])
    if (!level) continue
    const rest = parts.slice(i + 1).join('/')
    if (level.listOnly && hasParam(rest)) return null
    return [parts.slice(0, i + 1).join('/'), rest, level]
  }
  return null
}

/** Groups children under one parent per level found, recursing into each group. */
export const groupLevels = (children: RouteLike[], levels: TabLevel[], makeTabParent: TabParentFactory): RouteLike[] => {
  if (!levels.length) return children

  const groups = new Map<string, { level: TabLevel; kids: RouteLike[] }>()
  const direct: RouteLike[] = []

  children.forEach((child) => {
    const split = !child.customTabs && typeof child.path === 'string' ? splitAtLevel(child.path, levels) : null
    if (!split) {
      direct.push(child)
      return
    }
    const [prefix, rest, level] = split
    if (!groups.has(prefix)) groups.set(prefix, { level, kids: [] })
    groups.get(prefix)!.kids.push({ ...child, path: rest })
  })

  return [
    ...direct,
    ...Array.from(groups.entries()).map(([prefix, { level, kids }]) =>
      makeTabParent({
        path: prefix,
        page: level.page,
        variant: level.variant,
        // Drop this level so the recursion terminates; deeper levels still apply.
        children: groupLevels(
          kids,
          levels.filter((candidate) => candidate !== level),
          makeTabParent,
        ),
      }),
    ),
  ]
}

/**
 * Wraps the project-management subtree: one Row 1 strip per permissions endpoint, then the
 * declared levels beneath it.
 *
 * Two Row 1 parents rather than one because TabsLayout picks its permissions endpoint from
 * page/parentPage: linked projects must read getLinkedProjectPermissions. They share the same
 * `:name/:id` path, which is fine — React Router ranks on full child paths and the two child
 * sets are disjoint.
 */
export const hoistProjectTabs = (routeList: RouteLike[], levels: TabLevel[], makeTabParent: TabParentFactory): RouteLike[] =>
  routeList.map((route) => {
    if (route?.path !== 'project-management' || !Array.isArray(route.children)) return route

    const scoped: RouteLike[] = []
    const linked: RouteLike[] = []
    const rest: RouteLike[] = []

    route.children.forEach((child) => {
      const path = typeof child.path === 'string' ? child.path : ''
      const relative = path.startsWith(`${PROJECT_SCOPE}/`) ? path.slice(PROJECT_SCOPE.length + 1) : null
      if (relative === null) {
        rest.push(child)
        return
      }
      // Keyed on the segment, not on `:subsidiaryId` — nai-flow-view routes exist both with and
      // without that param, and both need the linked-project permissions endpoint.
      ;(relative.split('/').includes('nai-flow-view') ? linked : scoped).push({ ...child, path: relative })
    })

    const row1 = (children: RouteLike[], parentPage?: string) =>
      makeTabParent({ path: PROJECT_SCOPE, page: 'project-page', variant: 'secondary', parentPage, children: groupLevels(children, levels, makeTabParent) })

    return {
      ...route,
      children: [...rest, ...(scoped.length ? [row1(scoped)] : []), ...(linked.length ? [row1(linked, 'nai-flow-view')] : [])],
    }
  })

/**
 * Applies `hoistLevels` wherever a route declares it, so blocks outside project management (the
 * dashboard) get the same treatment without a second bespoke function. Children are processed
 * first, so nested markers work.
 *
 * project-management keeps its own entry point because of the `:name/:id` scope and the
 * linked/non-linked split, which no generic marker can express.
 */
export const applyTabLevels = (routeList: RouteLike[], makeTabParent: TabParentFactory): RouteLike[] =>
  routeList.map((route) => {
    if (!Array.isArray(route.children)) return route
    const processed = applyTabLevels(route.children, makeTabParent)
    const levelled = route.hoistLevels ? groupLevels(processed, route.hoistLevels, makeTabParent) : processed
    // `hoistStrip` wraps a whole block in one strip shared by all its children — the block's own
    // top level, which has no segment to key on. Applied last so it sits outside the levels.
    const children = route.hoistStrip ? [makeTabParent({ path: '', ...route.hoistStrip, children: levelled })] : levelled
    return { ...route, children }
  })
